import {
  ActiveOnSiteSegmentFields,
  BouncedEmailSegmentFields,
  CheckoutStartedSegmentFields,
  ClickedEmailSegmentFields,
  ClickedTextSegmentFields,
  CollectionViewedSegmentFields,
  CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE,
  findSegmentField,
  MarketingSubscribedSegmentFields,
  OpenedEmailSegmentFields,
  OrderPlacedSegmentFields,
  ProductAddedToCartSegmentFields,
  ProductViewedSegmentFields,
  ReceivedEmailSegmentFields,
  ReceivedTextSegmentFields,
  ReturnProcessedSegmentFields,
  type SegmentDataStructureType,
  SubscribedToBackInStockSegmentFields,
  TriggeredBackInStockNotificationSegmentFields,
} from "./segment-data-structures";
import {
  CustomerActivityType,
  CustomerCharacteristicType,
} from "./segment-types";

/**
 * How the merchant segment builder (and public GraphQL token-options) load
 * allowed values for a field. One map, two callers — do not fork this.
 */
export enum FetchStrategy {
  AUTOMATION = "automation",
  CAMPAIGN = "campaign",
  COLLECTION_HANDLE = "collection-handle",
  COLLECTION_NAME = "collection-name",
  COLLECTION_ID = "collection-id",
  PRODUCT_VENDOR = "product-vendor",
  BACKEND_SEARCH = "backend-search",
  NONE = "none",
  RETURN_TYPE = "return-type",
  BOUNCE_TYPE = "bounce-type",
  CUSTOMER_TAGS = "customer-tags",
  STATIC_SEGMENT_MEMBERSHIP = "static-segment-membership",
  STATES = "states",
  COUNTRIES = "countries",
  CITIES = "cities",
  CUSTOM_FIELDS = "custom-fields",
  ENTRY_POINT = "entry-point",
  CHANNEL = "channel",
}

export interface SegmentTokenStrategyKey {
  dataStructure: SegmentDataStructureType;
  dataStructureField: string;
}

const customerCharacteristicTokenFetchStrategies: Record<
  CustomerCharacteristicType,
  FetchStrategy
> = {
  [CustomerCharacteristicType.COUNTRY]: FetchStrategy.COUNTRIES,
  [CustomerCharacteristicType.CREATED_TIME]: FetchStrategy.NONE,
  [CustomerCharacteristicType.BIRTHDAY]: FetchStrategy.NONE,
  [CustomerCharacteristicType.RETURN_RATE_COUNT]: FetchStrategy.NONE,
  [CustomerCharacteristicType.LIFETIME_REVENUE]: FetchStrategy.NONE,
  [CustomerCharacteristicType.RETURN_RATE_VALUE]: FetchStrategy.NONE,
  [CustomerCharacteristicType.STATE_PROVINCE]: FetchStrategy.STATES,
  [CustomerCharacteristicType.STATIC_SEGMENT_MEMBERSHIP]:
    FetchStrategy.STATIC_SEGMENT_MEMBERSHIP,
  [CustomerCharacteristicType.SUBSCRIBED_TO_EMAIL]: FetchStrategy.NONE,
  [CustomerCharacteristicType.CAN_RECEIVE_EMAIL_MARKETING]: FetchStrategy.NONE,
  [CustomerCharacteristicType.SUBSCRIBED_TO_SMS]: FetchStrategy.NONE,
  [CustomerCharacteristicType.CUSTOMER_TAGS]: FetchStrategy.CUSTOMER_TAGS,
  [CustomerCharacteristicType.CITY]: FetchStrategy.CITIES,
  [CustomerCharacteristicType.PROXIMITY_TO_CITY]: FetchStrategy.CITIES,
  [CustomerCharacteristicType.CUSTOM_FIELDS]: FetchStrategy.CUSTOM_FIELDS,
  [CustomerCharacteristicType.PHONE_NUMBER_AREA_CODE]: FetchStrategy.NONE,
  [CustomerCharacteristicType.ENROLLED_IN_LOYALTY_LION]: FetchStrategy.NONE,
  [CustomerCharacteristicType.EMAIL_ADDRESS]: FetchStrategy.NONE,
  [CustomerCharacteristicType.CUSTOMER_NAME]: FetchStrategy.NONE,
};

const orderPlacedTokenFetchStrategies: Record<
  OrderPlacedSegmentFields,
  FetchStrategy
> = {
  [OrderPlacedSegmentFields.ITEM_COUNT]: FetchStrategy.NONE,
  [OrderPlacedSegmentFields.ORDER_TOTAL]: FetchStrategy.NONE,
  [OrderPlacedSegmentFields.DELIVERY_METHOD]: FetchStrategy.BACKEND_SEARCH,
  [OrderPlacedSegmentFields.PRODUCT_ID]: FetchStrategy.BACKEND_SEARCH,
  [OrderPlacedSegmentFields.PRODUCT_NAME]: FetchStrategy.BACKEND_SEARCH,
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_ID]: FetchStrategy.BACKEND_SEARCH,
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_NAME]: FetchStrategy.BACKEND_SEARCH,
  [OrderPlacedSegmentFields.PRODUCT_VARIANT_SKU]: FetchStrategy.BACKEND_SEARCH,
  [OrderPlacedSegmentFields.COLLECTION_ID]: FetchStrategy.COLLECTION_ID,
  [OrderPlacedSegmentFields.COLLECTION_NAME]: FetchStrategy.COLLECTION_HANDLE,
  [OrderPlacedSegmentFields.PRODUCT_TAG]: FetchStrategy.BACKEND_SEARCH,
  [OrderPlacedSegmentFields.VENDOR]: FetchStrategy.PRODUCT_VENDOR,
};

const productViewedTokenFetchStrategies: Record<
  ProductViewedSegmentFields,
  FetchStrategy
> = {
  [ProductViewedSegmentFields.PRODUCT_ID]: FetchStrategy.BACKEND_SEARCH,
  [ProductViewedSegmentFields.PRODUCT_NAME]: FetchStrategy.BACKEND_SEARCH,
  [ProductViewedSegmentFields.PRODUCT_VARIANT_ID]: FetchStrategy.BACKEND_SEARCH,
  [ProductViewedSegmentFields.PRODUCT_VARIANT_NAME]:
    FetchStrategy.BACKEND_SEARCH,
  [ProductViewedSegmentFields.PRODUCT_VARIANT_SKU]:
    FetchStrategy.BACKEND_SEARCH,
  [ProductViewedSegmentFields.COLLECTION_NAME]: FetchStrategy.COLLECTION_HANDLE,
  [ProductViewedSegmentFields.PRODUCT_TAGS]: FetchStrategy.BACKEND_SEARCH,
};

const collectionViewedTokenFetchStrategies: Record<
  CollectionViewedSegmentFields,
  FetchStrategy
> = {
  [CollectionViewedSegmentFields.COLLECTION_ID]: FetchStrategy.COLLECTION_ID,
  [CollectionViewedSegmentFields.COLLECTION_NAME]:
    FetchStrategy.COLLECTION_NAME,
};

const receivedEmailTokenFetchStrategies: Record<
  ReceivedEmailSegmentFields,
  FetchStrategy
> = {
  [ReceivedEmailSegmentFields.CAMPAIGN]: FetchStrategy.CAMPAIGN,
  [ReceivedEmailSegmentFields.AUTOMATION]: FetchStrategy.AUTOMATION,
};

const openedEmailTokenFetchStrategies: Record<
  OpenedEmailSegmentFields,
  FetchStrategy
> = {
  [OpenedEmailSegmentFields.CAMPAIGN]: FetchStrategy.CAMPAIGN,
  [OpenedEmailSegmentFields.AUTOMATION]: FetchStrategy.AUTOMATION,
};

const clickedEmailTokenFetchStrategies: Record<
  ClickedEmailSegmentFields,
  FetchStrategy
> = {
  [ClickedEmailSegmentFields.CAMPAIGN]: FetchStrategy.CAMPAIGN,
  [ClickedEmailSegmentFields.AUTOMATION]: FetchStrategy.AUTOMATION,
};

const receivedTextTokenFetchStrategies: Record<
  ReceivedTextSegmentFields,
  FetchStrategy
> = {
  [ReceivedTextSegmentFields.CAMPAIGN]: FetchStrategy.CAMPAIGN,
  [ReceivedTextSegmentFields.AUTOMATION]: FetchStrategy.AUTOMATION,
};

const clickedTextTokenFetchStrategies: Record<
  ClickedTextSegmentFields,
  FetchStrategy
> = {
  [ClickedTextSegmentFields.CAMPAIGN]: FetchStrategy.CAMPAIGN,
  [ClickedTextSegmentFields.AUTOMATION]: FetchStrategy.AUTOMATION,
};

const bouncedEmailTokenFetchStrategies: Record<
  BouncedEmailSegmentFields,
  FetchStrategy
> = {
  [BouncedEmailSegmentFields.CAMPAIGN]: FetchStrategy.CAMPAIGN,
  [BouncedEmailSegmentFields.AUTOMATION]: FetchStrategy.AUTOMATION,
  [BouncedEmailSegmentFields.BOUNCE_TYPE]: FetchStrategy.BOUNCE_TYPE,
};

const addedToCartTokenFetchStrategies: Record<
  ProductAddedToCartSegmentFields,
  FetchStrategy
> = {
  [ProductAddedToCartSegmentFields.PRODUCT_ID]: FetchStrategy.BACKEND_SEARCH,
  [ProductAddedToCartSegmentFields.PRODUCT_NAME]: FetchStrategy.BACKEND_SEARCH,
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_ID]:
    FetchStrategy.BACKEND_SEARCH,
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_NAME]:
    FetchStrategy.BACKEND_SEARCH,
  [ProductAddedToCartSegmentFields.PRODUCT_VARIANT_SKU]:
    FetchStrategy.BACKEND_SEARCH,
  [ProductAddedToCartSegmentFields.PRODUCT_COLLECTION]:
    FetchStrategy.COLLECTION_ID,
  [ProductAddedToCartSegmentFields.PRODUCT_TAGS]: FetchStrategy.BACKEND_SEARCH,
  [ProductAddedToCartSegmentFields.QUANTITY]: FetchStrategy.NONE,
  [ProductAddedToCartSegmentFields.CART_SUBTOTAL]: FetchStrategy.NONE,
};

const checkoutStartedToFetchStrategies: Record<
  CheckoutStartedSegmentFields,
  FetchStrategy
> = {
  [CheckoutStartedSegmentFields.PRODUCT_ID]: FetchStrategy.BACKEND_SEARCH,
  [CheckoutStartedSegmentFields.PRODUCT_NAME]: FetchStrategy.BACKEND_SEARCH,
  [CheckoutStartedSegmentFields.PRODUCT_VARIANT_ID]:
    FetchStrategy.BACKEND_SEARCH,
  [CheckoutStartedSegmentFields.PRODUCT_VARIANT_NAME]:
    FetchStrategy.BACKEND_SEARCH,
  [CheckoutStartedSegmentFields.PRODUCT_VARIANT_SKU]:
    FetchStrategy.BACKEND_SEARCH,
  [CheckoutStartedSegmentFields.PRODUCT_COLLECTION]:
    FetchStrategy.COLLECTION_ID,
  [CheckoutStartedSegmentFields.PRODUCT_TAGS]: FetchStrategy.BACKEND_SEARCH,
  [CheckoutStartedSegmentFields.QUANTITY]: FetchStrategy.NONE,
};

const activeOnSiteToFetchStrategies: Record<
  ActiveOnSiteSegmentFields,
  FetchStrategy
> = {
  [ActiveOnSiteSegmentFields.URL_WITHOUT_PARAMS]: FetchStrategy.BACKEND_SEARCH,
};

const returnProcessedToFetchStrategies: Record<
  ReturnProcessedSegmentFields,
  FetchStrategy
> = {
  [ReturnProcessedSegmentFields.RETURN_TYPE]: FetchStrategy.RETURN_TYPE,
  [ReturnProcessedSegmentFields.PRODUCT_NAMES]: FetchStrategy.BACKEND_SEARCH,
  [ReturnProcessedSegmentFields.PRODUCT_VARIANT_NAMES]:
    FetchStrategy.BACKEND_SEARCH,
  [ReturnProcessedSegmentFields.PRODUCT_SKUS]: FetchStrategy.BACKEND_SEARCH,
  [ReturnProcessedSegmentFields.PRODUCT_ID]: FetchStrategy.BACKEND_SEARCH,
  [ReturnProcessedSegmentFields.PRODUCT_VARIANT_ID]:
    FetchStrategy.BACKEND_SEARCH,
  [ReturnProcessedSegmentFields.PRODUCT_TAGS]: FetchStrategy.BACKEND_SEARCH,
  [ReturnProcessedSegmentFields.COLLECTION_NAMES]:
    FetchStrategy.COLLECTION_HANDLE,
};

const subscribedToBackInStockTokenFetchStrategies: Record<
  SubscribedToBackInStockSegmentFields,
  FetchStrategy
> = {
  [SubscribedToBackInStockSegmentFields.PRODUCT_ID]:
    FetchStrategy.BACKEND_SEARCH,
  [SubscribedToBackInStockSegmentFields.PRODUCT_NAME]:
    FetchStrategy.BACKEND_SEARCH,
  [SubscribedToBackInStockSegmentFields.VARIANT_ID]:
    FetchStrategy.BACKEND_SEARCH,
  [SubscribedToBackInStockSegmentFields.PRODUCT_VARIANT_NAME]:
    FetchStrategy.BACKEND_SEARCH,
  [SubscribedToBackInStockSegmentFields.PRODUCT_VARIANT_SKU]:
    FetchStrategy.BACKEND_SEARCH,
  [SubscribedToBackInStockSegmentFields.COLLECTION_NAME]:
    FetchStrategy.COLLECTION_HANDLE,
  [SubscribedToBackInStockSegmentFields.PRODUCT_TAGS]:
    FetchStrategy.BACKEND_SEARCH,
  [SubscribedToBackInStockSegmentFields.VENDOR]: FetchStrategy.PRODUCT_VENDOR,
};

const triggeredBackInStockNotificationTokenFetchStrategies: Record<
  TriggeredBackInStockNotificationSegmentFields,
  FetchStrategy
> = {
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_ID]:
    FetchStrategy.BACKEND_SEARCH,
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_NAME]:
    FetchStrategy.BACKEND_SEARCH,
  [TriggeredBackInStockNotificationSegmentFields.VARIANT_ID]:
    FetchStrategy.BACKEND_SEARCH,
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_VARIANT_NAME]:
    FetchStrategy.BACKEND_SEARCH,
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_VARIANT_SKU]:
    FetchStrategy.BACKEND_SEARCH,
  [TriggeredBackInStockNotificationSegmentFields.COLLECTION_NAME]:
    FetchStrategy.COLLECTION_HANDLE,
  [TriggeredBackInStockNotificationSegmentFields.PRODUCT_TAGS]:
    FetchStrategy.BACKEND_SEARCH,
  [TriggeredBackInStockNotificationSegmentFields.VENDOR]:
    FetchStrategy.PRODUCT_VENDOR,
};

const marketingSubscribedTokenFetchStrategies: Record<
  MarketingSubscribedSegmentFields,
  FetchStrategy
> = {
  [MarketingSubscribedSegmentFields.ENTRY_POINT]: FetchStrategy.ENTRY_POINT,
  [MarketingSubscribedSegmentFields.CHANNEL]: FetchStrategy.CHANNEL,
};

export const TokenFetchStrategies: Record<
  SegmentDataStructureType,
  Record<string, FetchStrategy>
> = {
  [CUSTOMER_CHARACTERISTIC_DATA_STRUCTURE]:
    customerCharacteristicTokenFetchStrategies,
  [CustomerActivityType.ACTIVE_ON_SITE_SHOPIFY]: activeOnSiteToFetchStrategies,
  [CustomerActivityType.ACTIVE_ON_SITE_COMMENTSOLD]:
    activeOnSiteToFetchStrategies,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_SHOPIFY]:
    addedToCartTokenFetchStrategies,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_COMMENTSOLD]:
    addedToCartTokenFetchStrategies,
  [CustomerActivityType.CHECKOUT_STARTED_SHOPIFY]:
    checkoutStartedToFetchStrategies,
  [CustomerActivityType.CHECKOUT_STARTED_COMMENTSOLD]:
    checkoutStartedToFetchStrategies,
  [CustomerActivityType.ADDED_PRODUCT_TO_CART_BIGCOMMERCE]:
    addedToCartTokenFetchStrategies,
  [CustomerActivityType.CHECKOUT_STARTED_BIGCOMMERCE]:
    checkoutStartedToFetchStrategies,
  [CustomerActivityType.ORDER_PLACED_BIGCOMMERCE]:
    orderPlacedTokenFetchStrategies,
  [CustomerActivityType.ORDER_PLACED_SHOPIFY]: orderPlacedTokenFetchStrategies,
  [CustomerActivityType.ORDER_PLACED_COMMENTSOLD]:
    orderPlacedTokenFetchStrategies,
  [CustomerActivityType.VIEWED_PRODUCT_SHOPIFY]:
    productViewedTokenFetchStrategies,
  [CustomerActivityType.VIEWED_PRODUCT_COMMENTSOLD]:
    productViewedTokenFetchStrategies,
  [CustomerActivityType.COLLECTION_VIEWED_SHOPIFY]:
    collectionViewedTokenFetchStrategies,
  [CustomerActivityType.COLLECTION_VIEWED_COMMENTSOLD]:
    collectionViewedTokenFetchStrategies,
  [CustomerActivityType.PAYMENT_FAILED_NO_ORDER_CREATED_SHOPIFY]: {},
  [CustomerActivityType.PAYMENT_FAILED_AFTER_ORDER_CREATED_SHOPIFY]: {},
  [CustomerActivityType.RECEIVED_EMAIL]: receivedEmailTokenFetchStrategies,
  [CustomerActivityType.OPENED_EMAIL]: openedEmailTokenFetchStrategies,
  [CustomerActivityType.CLICKED_EMAIL]: clickedEmailTokenFetchStrategies,
  [CustomerActivityType.RECEIVED_TEXT]: receivedTextTokenFetchStrategies,
  [CustomerActivityType.CLICKED_TEXT]: clickedTextTokenFetchStrategies,
  [CustomerActivityType.RETURN_PROCESSED]: returnProcessedToFetchStrategies,
  [CustomerActivityType.SUBSCRIBED_TO_BACK_IN_STOCK]:
    subscribedToBackInStockTokenFetchStrategies,
  [CustomerActivityType.TRIGGERED_BACK_IN_STOCK_NOTIFICATION]:
    triggeredBackInStockNotificationTokenFetchStrategies,
  [CustomerActivityType.BOUNCED_EMAIL]: bouncedEmailTokenFetchStrategies,
  [CustomerActivityType.REPLIED_SMS]: {},
  [CustomerActivityType.MARKETING_SUBSCRIBED]:
    marketingSubscribedTokenFetchStrategies,
  [CustomerActivityType.MARKETING_CONFIRMED]: {},
};

export function getTokenFetchStrategy(
  key: SegmentTokenStrategyKey,
): FetchStrategy {
  const field = findSegmentField(key.dataStructure, key.dataStructureField);
  const baseFieldName = field?.baseFieldName || key.dataStructureField;
  return (
    TokenFetchStrategies[key.dataStructure]?.[baseFieldName] ||
    FetchStrategy.NONE
  );
}
